export const formatBytes = (bytes: number | null): string => {
  if (bytes === null) return "";
  if (bytes < 1024 * 1024) return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const formatTime = (iso: string | null): string => (iso ? new Date(iso).toLocaleTimeString() : "");

/** The ready line. HQ does hold the zip until it is downloaded or expires, so it says that. */
export const readyMessage = (download: { fileName: string | null; bytes: number | null; expiresAt: string | null }): string =>
  `${download.fileName} (${formatBytes(download.bytes)}) is ready. HQ holds it until you download it, ` +
  `or until ${formatTime(download.expiresAt)} if you do not, and then deletes it.`;

/** `1 file` / `2 files`. */
export const counted = (n: number, one: string, many: string): string => `${n.toLocaleString()} ${n === 1 ? one : many}`;

/**
 * What a Bulk Export plan counts. One Titan table can feed several IntraSync module services
 * (Customers feeds core customers, accounting customers and tax-exemption certificates), and the
 * export writes one JSON file per service. The screen called both "tables", so a map of 160 files
 * from 133 Titan tables read as "160 mapped tables" beside "133 of 133 Titan tables" (item #3980).
 */
export interface PlanCounts {
  /** Export files: one per module service (and sub-table). */
  files: number;
  /** Distinct Titan tables those files are read from. */
  titanTables: number;
  /** Of those Titan tables, how many the connected source has; null with no source connected. */
  titanTablesInSource: number | null;
}

export function planCounts(entries: ReadonlyArray<{ titanTable: string; inSource: boolean | null }>): PlanCounts {
  const tables = new Map<string, boolean | null>();
  for (const e of entries) {
    const key = e.titanTable.toLowerCase();
    // A table is in the source when any of its files says so (they all read the same table).
    tables.set(key, tables.get(key) === true ? true : e.inSource);
  }
  const known = [...tables.values()];
  return {
    files: entries.length,
    titanTables: tables.size,
    titanTablesInSource: known.length > 0 && known.every((v) => v === null) ? null : known.filter((v) => v === true).length,
  };
}

/** A batch's chip: its files and the Titan tables they read. */
export const batchCountLabel = (entries: ReadonlyArray<{ titanTable: string; inSource: boolean | null }>): string => {
  const c = planCounts(entries);
  return `${counted(c.files, "file", "files")} from ${counted(c.titanTables, "Titan table", "Titan tables")}`;
};

/** A run's one-line summary, counting files and Titan tables apart. */
export function runSummaryLine(
  summary: { totalRows: number; exportedRows: number; invalidRows: number; errorTables: number },
  results: ReadonlyArray<{ titanTable: string }>
): string {
  const tables = new Set(results.map((r) => r.titanTable.toLowerCase())).size;
  return (
    `${counted(results.length, "export file", "export files")} from ${counted(tables, "Titan table", "Titan tables")}: ` +
    `${counted(summary.totalRows, "source row", "source rows")} read, ${summary.exportedRows.toLocaleString()} exported, ` +
    `${summary.invalidRows.toLocaleString()} invalid` +
    (summary.errorTables > 0 ? `, ${counted(summary.errorTables, "file", "files")} with errors.` : ".")
  );
}
